import React, { useEffect } from 'react';
import { useAIView } from '@/context/AIViewContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { AIChatContent } from './AIChatContent';
import { cn } from '@/lib/core/utils';

interface AIFloatingWindowProps {
  businessId: string | null;
  /** Mode: 'assistant' for dashboard (full features), 'sales' for landing page (minimal) */
  mode?: 'assistant' | 'sales';
  /** Initial message to send when chat opens (sales mode) */
  initialMessage?: string | null;
}

const WINDOW_WIDTH = 420;
const WINDOW_HEIGHT = 600;

export const AIFloatingWindow: React.FC<AIFloatingWindowProps> = ({
  businessId,
  mode = 'assistant',
  initialMessage,
}) => {
  const { viewMode, close, openSidebar } = useAIView();
  const isMobile = useIsMobile();

  const isOpen = viewMode === 'floating';

  // Handle escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, close]);

  // Switch to sidebar mode
  const handleToggleToSidebar = () => {
    openSidebar();
  };

  // Don't render on mobile
  if (isMobile) return null;

  return (
    <div
      className={cn(
        'fixed z-50 right-6 bottom-6',
        'rounded-2xl shadow-xl',
        'flex flex-col overflow-hidden',
        'border border-border',
        'bg-background',
        // Shrink/expand animation
        'transition-all duration-300 ease-out origin-bottom-right',
        isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
      )}
      style={{
        width: WINDOW_WIDTH,
        height: WINDOW_HEIGHT,
      }}
      role="dialog"
      aria-modal="false"
      aria-label="AI Assistant - Floating Window"
    >
      {/* Content - always render to preserve state when switching between floating/sidebar */}
      <div className="flex-1 min-h-0">
        <AIChatContent
          businessId={businessId}
          variant="floating"
          mode={mode}
          initialMessage={initialMessage}
          onClose={close}
          onToggleFullscreen={handleToggleToSidebar}
        />
      </div>
    </div>
  );
};

export default AIFloatingWindow;
